import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { resolveStorageUrl } from '../services/api';
import Button from '../components/Button';
import PublicShell, { PublicCard, PublicError, PublicPasswordGate } from '../components/PublicShell';
import { DRIVE_API_URL } from '../api/endpoints';

// A stable per-browser id for the "Access Stats" panel (View Stats). This
// page is cross-origin from the API and has no session of its own, so a
// client-generated id in localStorage stands in for a cookie — good enough
// to tell "the same visitor came back" apart from "a new one showed up".
function getGuestId(): string {
  const KEY = 'sk_drive_guest_id';
  try {
    const existing = localStorage.getItem(KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
    return id;
  } catch {
    // Storage disabled (private mode, etc.) — fall back to a per-load id;
    // it just won't be recognized as a returning guest next time.
    return crypto.randomUUID();
  }
}

interface LinkPayload {
  name: string;
  preview_url: string;
  download_url?: string;
  allow_print: boolean;
}

export default function SharedView() {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<'loading' | 'password' | 'ready' | 'error'>('loading');
  const [name, setName] = useState('');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [allowPrint, setAllowPrint] = useState(true);
  const [password, setPassword] = useState('');
  const [checking, setChecking] = useState(false);
  const [pwError, setPwError] = useState('');
  const [error, setError] = useState('');

  async function load(pw?: string) {
    if (!token) return;
    const params = new URLSearchParams({ guest: getGuestId() });
    if (pw) params.set('password', pw);
    try {
      const r = await fetch(`${DRIVE_API_URL}/s/${token}?${params}`);
      if (!r.ok) {
        const body = await r.json().catch(() => null);
        const message: string = body?.message ?? 'Link unavailable';
        if (r.status === 401) {
          // A 401 on the very first (password-less) load just means one is
          // needed; only treat it as "wrong" once a password was actually tried.
          setPwError(pw ? 'That password is not correct.' : '');
          setPhase('password');
          return;
        }
        setError(message);
        setPhase('error');
        return;
      }
      const data: LinkPayload = await r.json();
      setName(data.name);
      setPreviewUrl(resolveStorageUrl(data.preview_url));
      if (data.download_url) setDownloadUrl(resolveStorageUrl(data.download_url));
      setAllowPrint(data.allow_print);
      setPhase('ready');
    } catch {
      setError('This link is invalid or has expired.');
      setPhase('error');
    }
  }

  useEffect(() => { void load(); }, [token]);

  async function submitPassword() {
    setChecking(true);
    setPwError('');
    await load(password);
    setChecking(false);
  }

  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'avif'].includes(ext);
  const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(ext);

  const handleDownload = async () => {
    if (!downloadUrl || !token) return;
    // Best-effort — the download itself goes straight to the signed storage
    // URL, never through this API, so this is the only signal the Access
    // Stats panel gets that a download actually happened.
    fetch(`${DRIVE_API_URL}/s/${token}/downloaded?guest=${encodeURIComponent(getGuestId())}`, { method: 'POST' }).catch(() => {});
    const resp = await fetch(downloadUrl);
    const blob = await resp.blob();
    const obj = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = obj; a.download = name;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(obj), 1000);
  };

  return (
    <PublicShell>
      {/* Printing can't be blocked server-side — window.print() is a browser
          action the page can't intercept — so this hides everything under a
          print media query and leaves a visible notice instead. Real print
          prevention isn't achievable from a web page; this is the honest
          approximation "disable printing where applicable" can mean here. */}
      {!allowPrint && (
        <style>{'@media print { body * { visibility: hidden !important; } body::after { content: "Printing is disabled for this link."; visibility: visible !important; position: fixed; top: 40%; left: 0; right: 0; text-align: center; font: 16px sans-serif; } }'}</style>
      )}

      {phase === 'loading' && (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 200, height: 160, borderRadius: 12, background: '#e5e7eb', animation: 'pulse 1.5s infinite' }} />
          <div style={{ width: 140, height: 18, borderRadius: 6, background: '#e5e7eb' }} />
        </div>
      )}

      {phase === 'error' && <PublicError title="Link unavailable" message={error} />}

      {phase === 'password' && (
        <PublicPasswordGate
          label="This link is password protected"
          value={password}
          onChange={setPassword}
          onSubmit={() => void submitPassword()}
          busy={checking}
          error={pwError}
        />
      )}

      {phase === 'ready' && (
        <PublicCard>
          {/* Preview */}
          <div style={{ borderRadius: 10, overflow: 'hidden', marginBottom: 20, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 180 }}>
            {isImage && previewUrl ? (
              <img src={previewUrl} alt={name} style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain' }} />
            ) : isVideo && previewUrl ? (
              <video src={previewUrl} controls style={{ maxWidth: '100%', maxHeight: 320 }} />
            ) : (
              <div style={{ padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, color: '#6b7280' }}>
                <span style={{ fontSize: 48 }}>📄</span>
                <span style={{ fontSize: 13 }}>{ext.toUpperCase() || 'FILE'}</span>
              </div>
            )}
          </div>

          {/* Info */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontWeight: 700, fontSize: 18, color: '#1a1a2e', marginBottom: 4, wordBreak: 'break-word' }}>{name}</div>
            <div style={{ fontSize: 13, color: '#6b7280' }}>Shared via SK Drive</div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10 }}>
            {downloadUrl ? (
              <Button kind="accent" icon="download" onClick={handleDownload} block>
                Download
              </Button>
            ) : (
              <div style={{ fontSize: 13, color: '#6b7280', padding: '8px 0' }}>
                Downloads not enabled for this link
              </div>
            )}
          </div>
        </PublicCard>
      )}
    </PublicShell>
  );
}
